"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

interface BlackFridayStickyCtaProps {
	/** Anchor of the early-access form; the bar hides while that section is visible. */
	formId: string;
	href: string;
	label: string;
	note: string;
}

/**
 * Mobile Begleit-CTA. Er erscheint erst, nachdem der Hero aus dem Blick ist,
 * und verschwindet, sobald das Anmeldeformular sichtbar wird. So bleibt der
 * nächste Schritt in der Daumenzone, ohne das Formular zu überdecken.
 */
export function BlackFridayStickyCta({ formId, href, label, note }: BlackFridayStickyCtaProps) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const form = document.getElementById(formId);
		let frame = 0;

		const update = () => {
			frame = 0;
			const formRect = form?.getBoundingClientRect();
			const formInView = formRect ? formRect.top < window.innerHeight * 0.85 && formRect.bottom > 0 : false;
			setVisible(window.scrollY > 480 && !formInView);
		};

		const schedule = () => {
			if (frame === 0) frame = window.requestAnimationFrame(update);
		};

		update();
		window.addEventListener("scroll", schedule, { passive: true });
		window.addEventListener("resize", schedule);
		return () => {
			if (frame !== 0) window.cancelAnimationFrame(frame);
			window.removeEventListener("scroll", schedule);
			window.removeEventListener("resize", schedule);
		};
	}, [formId]);

	return (
		<div
			aria-hidden={!visible}
			inert={!visible}
			className={[
				"fixed inset-x-0 bottom-0 z-30 lg:hidden",
				"border-t border-border bg-card px-4 pt-3",
				"pb-[max(0.875rem,env(safe-area-inset-bottom))]",
				"shadow-[0_-6px_24px_rgba(0,0,0,0.08)]",
				"transition-transform duration-200 motion-reduce:transition-none",
				visible ? "translate-y-0" : "translate-y-full",
			].join(" ")}
		>
			<a
				href={href}
				className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg bg-primary px-4 py-3 text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			>
				<span className="min-w-0">
					<span className="block text-sm font-semibold leading-tight">{label}</span>
					<span className="mt-0.5 block text-[11px] leading-tight opacity-80">{note}</span>
				</span>
				<ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
			</a>
		</div>
	);
}
